import { useMemo } from "react";

import { BodySmall, BodyXS, Box, Radio } from "@parspec/pixel";

import { CompanyObjType } from "../../Company/queries/apiTypes";
import { Contact } from "../../Contacts/queries/apiTypes";
import { CRM_ENTITY_TYPE } from "../constant";
import { CrmCompany, CrmContact } from "../types";
import { getCombinedName } from "../utils";

type CommonAddress = Pick<CompanyObjType, 'email' | 'phone' | 'line1' | 'line2' | 'city' | 'state' | 'country'>;
type CompanyCard = Pick<CompanyObjType, 'id' | 'name' | 'website' | 'crm_id' | 'erp_id' | 'duns_hashtag'> & CommonAddress;
type ContactCard = Pick<Contact, 'id' | 'crm_id' | 'first_name' | 'last_name'> & { companyName: string } & CommonAddress;

interface RecordCheckboxCardProps {
    id: number | string;
    data: { record: CompanyCard; type: CrmCompany } | { record: ContactCard; type: CrmContact },
    checked: boolean;
    onCardClick: (id: number | string) => void;
    label?: string;
    labelSuffix?: React.ReactNode;
};

const formatPhone = (phone: string) => {
    if (!phone) return "";

    const phoneSets = phone.split("-");
    if (phoneSets.length !== 3) return phone;

    const [first, second, third] = phoneSets;
    return `(${first}) ${second}-${third}`;
};

const getAddressFields = (record: CompanyCard | ContactCard) => {
    const { email, phone, line1, city, state, country } = record;
    return [
        { title: 'Email', value: email, limit: true },
        { title: 'Phone', value: formatPhone(phone) },
        {
            title: 'Address',
            value: [line1, city, state, country].filter(it => !!it?.trim()).join(", ")
        }
    ];
}

const bodySmallStyle = { lineHeight: '1.25rem' };
const bodyXSStyle = { lineHeight: '1rem', overflowWrap: 'anywhere' };

function RecordCheckboxCard(props: RecordCheckboxCardProps) {
    const { id, data, checked, label, labelSuffix, onCardClick } = props;
    const { type, record } = data;

    const name = type === CRM_ENTITY_TYPE.COMPANY
        ? record.name
        : getCombinedName(record.first_name, record.last_name);

    const uniqueFields = useMemo(() => {
        if (type === CRM_ENTITY_TYPE.COMPANY) {
            const { crm_id, erp_id, duns_hashtag } = record;
            return [
                { title: 'CRM ID', value: crm_id },
                { title: 'ERP ID', value: erp_id },
                { title: 'DUNS #', value: duns_hashtag },
            ];
        }

        const { crm_id } = record;
        return [{ title: 'CRM ID', value: crm_id }];
    }, [type, record]);

    const detailFields = useMemo(() => {
        if (type === CRM_ENTITY_TYPE.COMPANY) {
            const { website } = record;
            return [
                { title: 'Website', value: website, limit: true },
                ...getAddressFields(record)
            ];
        }

        return [
            { title: 'Company Name', value: record.companyName },
            ...getAddressFields(record),
        ];
    }, [type, record])

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
            width="100%"
            border="1px solid"
            padding="8px 16px"
            borderRadius="6px"
            borderColor={checked ? "primary.main" : "neutral.main"}
            onClick={() => onCardClick(id)}
            sx={{ cursor: "pointer" }}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <Box display="flex" alignItems="center">
                    {/* negative margin helps align radio to left(as it has hover bg) */}
                    <Box marginLeft="-9px">
                        <Radio size="small" checked={checked} />
                    </Box>
                    {!!label && (
                        <BodySmall sx={bodySmallStyle} color="secondary.main" limit={false}>
                            {label}
                        </BodySmall>
                    )}
                </Box>

                {!!labelSuffix && <Box flexShrink={0}>{labelSuffix}</Box>}
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
                <BodySmall sx={bodySmallStyle} color="secondary.main" fontWeight={500} limit={false}>
                    {name}
                </BodySmall>

                {uniqueFields.map(field => {
                    const { title, value } = field;
                    return (
                        <Box key={title} display="flex" alignItems="center" gap={1}>
                            <BodyXS sx={bodyXSStyle} color="secondary.light" limit={false}>
                                {title}:
                            </BodyXS>
                            <BodyXS sx={bodyXSStyle} color="secondary.main" limit={false}>
                                {value ?? ""}
                            </BodyXS>
                        </Box>
                    );
                })}
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
                {detailFields.map(field => {
                    const { title, value, limit = false } = field;
                    return (
                        <Box key={title} display="flex" alignItems="flex-start" gap={1}>
                            <BodyXS sx={bodyXSStyle} color="secondary.light" flexShrink={0} limit={false}>
                                {`${title}:`}
                            </BodyXS>
                            <BodyXS sx={bodyXSStyle} color="secondary.main" limit={limit}>
                                {value ?? ""}
                            </BodyXS>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    )
};

export default RecordCheckboxCard;